// src/pages/CustomerNeedsPage.js
import React, { useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import NavBar from './components/NavBar';
import FilterBar from './components/FilterBar';
import theme from './theme';
import { DataAndFilterContext } from './context/DataAndFilterContext';
import { useGlobalLoading } from './context/GlobalLoadingContext';

// visuals (unchanged)
import NeedsImportanceVsSatisfaction from './components/data_visualizations/NeedsImportanceVsSatisfaction';
import NeedsImportanceAndSatisfactionOverTime from './components/data_visualizations/NeedsImportanceAndSatisfactionOverTime';
import NeedsCharacteristics from './components/data_visualizations/NeedsCharacteristics';
import NeedsSatisfactionWordClouds from './components/data_visualizations/NeedsSatisfactionWordClouds';
import NeedsSatisfactionAndImportanceDataTable from './components/data_visualizations/NeedsSatisfactionAndImportanceDataTable';
import NeedsImportanceSatisfactionScatter from './components/data_visualizations/NeedsImportanceSatisfactionScatter';

import JobsToBeDoneTiles from './components/needs/JobsToBeDoneTiles';
import JobMapAgnostic from './components/needs/JobMapAgnostic';
import JobMapsProductWorkflows from './components/needs/JobMapsProductWorkflows';

// ✅ Table Kit version of Jobs table
// If your file is named differently, point this import at your tablekit Jobs table.
import JobsTable from './components/detail/tables/JobsTable';

// small util
const slug = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, '-');

/**
 * Build parent + instance rows for JobsTable from the page's filteredData.
 * - No initial filtering here (contexts already scope it); we aggregate all jobs found.
 */
function useJobRowsFromStories(filteredData = []) {
  return useMemo(() => {
    // Map: jobSlug -> { name, instances: [], count, avgProductSat, href }
    const groups = new Map();

    // Pre-count stories that have at least one job (for pct denominator)
    const storiesWithJobs = filteredData.filter(s => Array.isArray(s?.jobs_to_be_done) && s.jobs_to_be_done.length > 0);
    const denom = storiesWithJobs.length || 1;

    for (const s of storiesWithJobs) {
      const jobs = Array.isArray(s.jobs_to_be_done) ? s.jobs_to_be_done : [];
      for (const j of jobs) {
        const name = j?.job_to_be_done_category || 'Uncategorized';
        const key = slug(name);

        if (!groups.has(key)) {
          groups.set(key, {
            name,
            instances: [],
            // We'll compute count/pct/avg later
          });
        }

        const instance = {
          name: s.customer_name || '—',
          date: s.conversation_date || '—',
          segmentation: s.segmentation_data || null,
          chosenSolution: s.solution_choice || s.solution_choice_category || '—',
          // Try to pull importance from reasonable places (schema can vary)
          importance: Number.isFinite(Number(j?.importance ?? j?.importance_score))
            ? Number(j?.importance ?? j?.importance_score)
            : null,
          // Product-level satisfaction commonly lives at story/root level in your data
          satisfaction: Number.isFinite(Number(
            s.satisfaction_level ?? s.product_satisfaction_level ?? s.solution_satisfaction_level
          )) ? Number(
            s.satisfaction_level ?? s.product_satisfaction_level ?? s.solution_satisfaction_level
          ) : null,
          // use satisfaction justification for the Summary column (robust to schema variants)
          summary:
            s?.satisfaction_justification ??
            s?.satisfaction_level_justification ??
            j?.satisfaction_justification ??
            j?.satisfaction_level_justification ??
            j?.job_summary ??
            s?.summary ??
            '—',
          
          href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        };

        groups.get(key).instances.push(instance);
      }
    }

    // Build parent rows
    const parents = [];
    for (const [, g] of groups) {
      const count = g.instances.length;

      // average product satisfaction across instances (0–10 scale used app-wide)
      const satVals = g.instances
        .map(i => (Number.isFinite(Number(i.satisfaction)) ? Number(i.satisfaction) : null))
        .filter(v => v != null);
      const avgProductSat = satVals.length
        ? satVals.reduce((a, b) => a + b, 0) / satVals.length
        : null;

      parents.push({
        key: slug(g.name),
        name: g.name,
        count,
        pct: Math.round((count / denom) * 100),
        avgProductSat,
        href: `#/jobs/${encodeURIComponent(g.name)}`, // placeholder; your router can replace later
        // sort instances by date desc (when possible)
        instances: g.instances.slice().sort((a, b) => {
          const ta = Date.parse(a.date || '') || 0;
          const tb = Date.parse(b.date || '') || 0;
          return tb - ta;
        })
      });
    }

    // default sorting: by count desc
    parents.sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));

    return parents;
  }, [filteredData]);
}

function CustomerNeedsPage() {
  const { loading } = useGlobalLoading();
  const { filteredData } = useContext(DataAndFilterContext);

  // 🔧 Build rows for JobsTable (no extra filtering; contexts already did that)
  const jobRows = useJobRowsFromStories(filteredData);

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
        <Box sx={{ padding: 3 }}>
          <Typography variant="h4" gutterBottom>
            Customer Needs
          </Typography>
          <FilterBar />

          <NeedsImportanceSatisfactionScatter />
          <br /><br />

          <JobMapAgnostic />
          <br /><br />

          {/* ---- Jobs Table (Table Kit) ---- */}
          <Typography variant="h6" gutterBottom>
            Jobs (frequency, share, satisfaction)
          </Typography>
          <JobsTable rows={jobRows} />
          {/* ------------------------------- */}

          {/* Existing visuals that use DataAndFilterContext */}
          {/* 
          <NeedsImportanceVsSatisfaction />
          <br /><br />
          <NeedsImportanceAndSatisfactionOverTime />
          <br /><br />
          <NeedsCharacteristics />
          <br /><br />
          <NeedsSatisfactionWordClouds />
          <br /><br />
          <NeedsSatisfactionAndImportanceDataTable />
          <br /><br />
          */}
        </Box>
    </ThemeProvider>
  );
}

export default CustomerNeedsPage;
